/**
 * App.tsx - Main application component
 * SwapyBook PWA - Book Exchange Platform
 * Provider hierarchy: Helmet > QueryClient > Theme > Language > Tooltip > Router
 */
import React, { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ScrollToTop, HashScrollHandler } from "@/components/ScrollToTop";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/hooks/useTheme";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";

// Lazy load all pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/auth/Auth"));
const BookCatalog = lazy(() => import("./pages/books/BookCatalog"));
const BookDetail = lazy(() => import("./pages/books/BookDetail"));
const MyBooks = lazy(() => import("./pages/books/MyBooks"));
const PublishBook = lazy(() => import("./pages/books/PublishBook"));
const UserDashboard = lazy(() => import("./pages/dashboard/UserDashboard"));
const Exchanges = lazy(() => import("./pages/exchanges/Exchanges"));
const Wishlist = lazy(() => import("./pages/exchanges/Wishlist"));
const Settings = lazy(() => import("./pages/settings/Settings"));
const Profile = lazy(() => import("./pages/settings/Profile"));
const Security = lazy(() => import("./pages/settings/Security"));
const Notifications = lazy(() => import("./pages/settings/Notifications"));
const AboutPage = lazy(() => import("./pages/static/AboutPage"));
const Contact = lazy(() => import("./pages/static/Contact"));
const Help = lazy(() => import("./pages/static/Help"));
const LegalPage = lazy(() => import("./pages/static/LegalPage"));
const Install = lazy(() => import("./pages/Install"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const BooksManagement = lazy(() => import("./pages/admin/BooksManagement"));
const ContactMessages = lazy(() => import("./pages/admin/ContactMessages"));
const Feedback = lazy(() => import("./pages/admin/Feedback"));
const LegalDocuments = lazy(() => import("./pages/admin/LegalDocuments"));
const SystemConfig = lazy(() => import("./pages/admin/SystemConfig"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));

// Create QueryClient instance outside component to prevent recreation on re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App(): React.JSX.Element {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <LanguageProvider>
            <TooltipProvider>
              <BrowserRouter>
                <Toaster />
                <Sonner />
                <AuthProvider>
                  <ScrollToTop />
                  <HashScrollHandler />
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/catalog" element={<BookCatalog />} />
                      <Route path="/book/:id" element={<BookDetail />} />
                      <Route path="/terms" element={<LegalPage />} />
                      <Route path="/privacy" element={<LegalPage />} />
                      <Route path="/help" element={<Help />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/install" element={<Install />} />
                      <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />
                      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                      <Route path="/settings/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                      <Route path="/settings/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
                      <Route path="/settings/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                      <Route path="/publish" element={<ProtectedRoute><PublishBook /></ProtectedRoute>} />
                      <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
                      <Route path="/my-books" element={<ProtectedRoute><MyBooks /></ProtectedRoute>} />
                      <Route path="/exchanges" element={<ProtectedRoute><Exchanges /></ProtectedRoute>} />
                      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
                      <Route path="/admin/legal" element={<ProtectedRoute requireAdmin><LegalDocuments /></ProtectedRoute>} />
                      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><UserManagement /></ProtectedRoute>} />
                      <Route path="/admin/feedback" element={<ProtectedRoute requireAdmin><Feedback /></ProtectedRoute>} />
                      <Route path="/admin/books" element={<ProtectedRoute requireAdmin><BooksManagement /></ProtectedRoute>} />
                      <Route path="/admin/config" element={<ProtectedRoute requireAdmin><SystemConfig /></ProtectedRoute>} />
                      <Route path="/admin/contact" element={<ProtectedRoute requireAdmin><ContactMessages /></ProtectedRoute>} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </AuthProvider>
              </BrowserRouter>
            </TooltipProvider>
          </LanguageProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
